import React, { useMemo, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Box, Stack, Typography, Button, IconButton,
  Select, MenuItem, TextField, Chip, Switch, FormControlLabel,
  Table, TableBody, TableCell, TableHead, TableRow, TableContainer, Paper, Divider, ListSubheader, Alert,
} from '@mui/material';
import { Add, Delete, AutoFixHigh, Save, Close, DragIndicator } from '@mui/icons-material';
import { ProductMapping, SubCatDef, catLabel, heuristicSubCat } from '../utils/massBalanceMapping';

interface Props {
  open: boolean;
  onClose: () => void;
  products: { name: string }[];
  mapping: ProductMapping;
  onChange: (m: ProductMapping) => void;
  onSave: () => Promise<void>;
  // Best sub-category for a not-yet-mapped product. Supplied by the parent so it can use the
  // dataset's own classification (e.g. the OAS dataset's Daily-Yields Group column) before the keyword heuristic.
  suggest?: (name: string) => string;
}

const slug = (s: string) => s.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '') || `s${Date.now()}`;

const MassBalanceMapping: React.FC<Props> = ({ open, onClose, products, mapping, onChange, onSave, suggest }) => {
  const [newSub, setNewSub] = useState<Record<string, string>>({});
  const [newCat, setNewCat] = useState('');
  const [unassignedOnly, setUnassignedOnly] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savedNote, setSavedNote] = useState<string | null>(null);
  const [dragCatId, setDragCatId] = useState<string | null>(null);
  const [dragSub, setDragSub] = useState<{ cat: string; id: string } | null>(null);

  // reorder MB categories
  const reorderCats = (targetId: string) => {
    if (!dragCatId || dragCatId === targetId) return;
    const cats = [...mapping.categories];
    const from = cats.findIndex((c) => c.id === dragCatId);
    const to = cats.findIndex((c) => c.id === targetId);
    if (from < 0 || to < 0) return;
    const [it] = cats.splice(from, 1); cats.splice(to, 0, it);
    onChange({ ...mapping, categories: cats });
  };
  // reorder sub-categories within a category (rebuild full array preserving other categories' slots)
  const rebuildSubs = (catId: string, reordered: SubCatDef[]) => {
    let ptr = 0;
    const rebuilt = mapping.subCategories.map((s) => (s.category === catId ? reordered[ptr++] : s));
    onChange({ ...mapping, subCategories: rebuilt });
  };
  const reorderSubs = (catId: string, targetId: string) => {
    if (!dragSub || dragSub.cat !== catId || dragSub.id === targetId) return;
    const catSubs = mapping.subCategories.filter((s) => s.category === catId);
    const from = catSubs.findIndex((s) => s.id === dragSub.id);
    const to = catSubs.findIndex((s) => s.id === targetId);
    if (from < 0 || to < 0) return;
    const [it] = catSubs.splice(from, 1); catSubs.splice(to, 0, it);
    rebuildSubs(catId, catSubs);
  };
  const moveSubToEnd = (catId: string) => {
    if (!dragSub || dragSub.cat !== catId) return;
    const catSubs = mapping.subCategories.filter((s) => s.category === catId);
    const from = catSubs.findIndex((s) => s.id === dragSub.id);
    if (from < 0 || from === catSubs.length - 1) return;
    const [it] = catSubs.splice(from, 1); catSubs.push(it);
    rebuildSubs(catId, catSubs);
  };

  const assignedCount = products.filter((p) => mapping.assignments[p.name]).length;

  const setAssignment = (name: string, sid: string) =>
    onChange({ ...mapping, assignments: { ...mapping.assignments, [name]: sid } });

  const addSub = (catId: string) => {
    const label = (newSub[catId] || '').trim();
    if (!label) return;
    const existing = new Set(mapping.subCategories.map((s) => s.id));
    const base = `${catId}-${slug(label)}`; let id = base; let i = 1;
    while (existing.has(id)) id = `${base}-${i++}`;
    onChange({ ...mapping, subCategories: [...mapping.subCategories, { id, label, category: catId }] });
    setNewSub({ ...newSub, [catId]: '' });
  };
  const removeSub = (id: string) => {
    const assignments = { ...mapping.assignments };
    Object.keys(assignments).forEach((k) => { if (assignments[k] === id) delete assignments[k]; });
    onChange({ ...mapping, subCategories: mapping.subCategories.filter((s) => s.id !== id), assignments });
  };
  const addCategory = () => {
    const label = newCat.trim();
    if (!label) return;
    const existing = new Set(mapping.categories.map((c) => c.id));
    const base = slug(label); let id = base; let i = 1;
    while (existing.has(id)) id = `${base}_${i++}`;
    onChange({ ...mapping, categories: [...mapping.categories, { id, label }] });
    setNewCat('');
  };
  const removeCategory = (catId: string) => {
    const subIds = new Set(mapping.subCategories.filter((s) => s.category === catId).map((s) => s.id));
    const assignments = { ...mapping.assignments };
    Object.keys(assignments).forEach((k) => { if (subIds.has(assignments[k])) delete assignments[k]; });
    onChange({ ...mapping, categories: mapping.categories.filter((c) => c.id !== catId), subCategories: mapping.subCategories.filter((s) => s.category !== catId), assignments });
  };
  const autoAssign = () => {
    const assignments = { ...mapping.assignments };
    products.forEach((p) => { if (!assignments[p.name]) { const s = (suggest && suggest(p.name)) || heuristicSubCat(p.name); if (s) assignments[p.name] = s; } });
    onChange({ ...mapping, assignments });
  };

  const subsByCat = useMemo(() => {
    const m: Record<string, SubCatDef[]> = {};
    mapping.categories.forEach((c) => { m[c.id] = []; });
    mapping.subCategories.forEach((s) => { (m[s.category] = m[s.category] || []).push(s); });
    return m;
  }, [mapping.subCategories, mapping.categories]);
  const sortedProducts = useMemo(() => [...products].sort((a, b) => a.name.localeCompare(b.name)), [products]);
  const unassignedCount = products.filter((p) => !mapping.assignments[p.name]).length;
  const tableProducts = (unassignedOnly && unassignedCount > 0) ? sortedProducts.filter((p) => !mapping.assignments[p.name]) : sortedProducts;

  const doSave = async () => {
    setSaving(true); setSavedNote(null);
    try { await onSave(); setSavedNote('Mapping saved — it will auto-apply to future reports.'); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ pr: 6 }}>
        Mass Balance Product Mapping
        <IconButton onClick={onClose} sx={{ position: 'absolute', right: 8, top: 8 }}><Close /></IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Sort each product into an <strong>MB Category</strong> (Feedstock / Intermediates / Products / By-Products) and a
          <strong> Sub Category</strong> (Crude, Gasoline, Distillate, LPG, Coke, …). Fuel family lives in the sub-category.
          Add or remove sub-categories, then <strong>Save</strong> — it's stored for the site and applied automatically to future reports.
        </Typography>

        {/* Category / sub-category management — drag the ⠿ handle to reorder cards; drag chips to reorder sub-categories */}
        <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Categories &amp; sub-categories</Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
          Drag the <DragIndicator sx={{ fontSize: 14, verticalAlign: 'middle' }} /> handle to reorder categories, and drag the chips to reorder sub-categories. That order is what shows in the report and the yield panel.
        </Typography>
        <Stack direction="row" spacing={2} sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
          {mapping.categories.map((c) => (
            <Paper key={c.id} variant="outlined"
              onDragOver={(e) => { if (dragCatId || (dragSub && dragSub.cat === c.id)) e.preventDefault(); }}
              onDrop={(e) => { e.preventDefault(); if (dragCatId) reorderCats(c.id); else if (dragSub && dragSub.cat === c.id) moveSubToEnd(c.id); }}
              sx={{ p: 1.5, flex: 1, minWidth: 220, opacity: dragCatId === c.id ? 0.45 : 1, transition: 'opacity .15s' }}>
              <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 0.5 }}>
                <Box draggable sx={{ display: 'flex', alignItems: 'center', gap: 0.5, cursor: 'grab', flex: 1, minWidth: 0 }}
                  onDragStart={(e) => { setDragCatId(c.id); setDragSub(null); e.dataTransfer.effectAllowed = 'move'; }}
                  onDragEnd={() => setDragCatId(null)}>
                  <DragIndicator fontSize="small" sx={{ color: 'text.disabled' }} />
                  <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 700, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{c.label}</Typography>
                </Box>
                <IconButton size="small" onClick={() => removeCategory(c.id)} title="Remove category"><Delete sx={{ fontSize: 16 }} /></IconButton>
              </Stack>
              <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ my: 1, minHeight: 24 }}>
                {(subsByCat[c.id] || []).map((s) => (
                  <Box key={s.id} draggable
                    onDragStart={(e) => { e.stopPropagation(); setDragSub({ cat: c.id, id: s.id }); setDragCatId(null); e.dataTransfer.effectAllowed = 'move'; }}
                    onDragEnd={() => setDragSub(null)}
                    onDragOver={(e) => { if (dragSub && dragSub.cat === c.id) { e.preventDefault(); } }}
                    onDrop={(e) => { if (dragSub && dragSub.cat === c.id) { e.preventDefault(); e.stopPropagation(); reorderSubs(c.id, s.id); } }}
                    sx={{ cursor: 'grab', opacity: dragSub?.id === s.id ? 0.45 : 1, display: 'inline-flex' }}>
                    <Chip label={s.label} size="small" onDelete={() => removeSub(s.id)} deleteIcon={<Delete />} />
                  </Box>
                ))}
                {(subsByCat[c.id] || []).length === 0 && <Typography variant="caption" color="text.disabled">no sub-categories</Typography>}
              </Stack>
              <Stack direction="row" spacing={0.5} alignItems="center">
                <TextField size="small" placeholder="Add sub-category…" value={newSub[c.id] || ''} variant="standard"
                  onChange={(e) => setNewSub({ ...newSub, [c.id]: e.target.value })}
                  onKeyDown={(e) => { if (e.key === 'Enter') addSub(c.id); }} sx={{ flex: 1 }} />
                <IconButton size="small" onClick={() => addSub(c.id)}><Add fontSize="small" /></IconButton>
              </Stack>
            </Paper>
          ))}
          <Paper variant="outlined" sx={{ p: 1.5, flex: 1, minWidth: 200, borderStyle: 'dashed', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
            <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 700, mb: 1 }}>Add group</Typography>
            <Stack direction="row" spacing={0.5} alignItems="center">
              <TextField size="small" placeholder="New MB category…" value={newCat} variant="standard"
                onChange={(e) => setNewCat(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') addCategory(); }} sx={{ flex: 1 }} />
              <IconButton size="small" onClick={addCategory}><Add fontSize="small" /></IconButton>
            </Stack>
          </Paper>
        </Stack>

        <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 1 }} flexWrap="wrap" useFlexGap>
          <Button size="small" variant="outlined" startIcon={<AutoFixHigh />} onClick={autoAssign}>Auto-assign remaining</Button>
          <FormControlLabel control={<Switch size="small" checked={mapping.intermediatesNegative}
            onChange={(e) => onChange({ ...mapping, intermediatesNegative: e.target.checked })} />}
            label={<Typography variant="body2">Intermediates as negative</Typography>} />
          <Box sx={{ flex: 1 }} />
          {unassignedCount > 0 && (
            <Chip size="small" clickable variant={unassignedOnly ? 'filled' : 'outlined'} color={unassignedOnly ? 'primary' : 'default'}
              label={unassignedOnly ? `Showing ${unassignedCount} unassigned` : `Show ${unassignedCount} unassigned`}
              onClick={() => setUnassignedOnly((v) => !v)} />
          )}
          <Chip size="small" color={assignedCount === products.length ? 'success' : 'default'}
            label={`${assignedCount}/${products.length} products assigned`} />
        </Stack>

        {savedNote && <Alert severity="success" sx={{ mb: 1 }} onClose={() => setSavedNote(null)}>{savedNote}</Alert>}

        <Divider sx={{ my: 1 }} />

        {/* Product assignment table */}
        <TableContainer sx={{ maxHeight: 440 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 700, bgcolor: 'background.paper' }}>Product</TableCell>
                <TableCell sx={{ fontWeight: 700, bgcolor: 'background.paper' }}>Category · Sub-category</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {tableProducts.map((p) => (
                <TableRow key={p.name} hover>
                  <TableCell>{p.name}</TableCell>
                  <TableCell>
                    <Select size="small" displayEmpty value={mapping.assignments[p.name] || ''} sx={{ minWidth: 260 }}
                      onChange={(e) => setAssignment(p.name, e.target.value)}
                      renderValue={(val) => {
                        const s = mapping.subCategories.find((x) => x.id === val);
                        return s ? `${catLabel(s.category)} · ${s.label}` : <em>Unassigned</em>;
                      }}>
                      <MenuItem value=""><em>Unassigned</em></MenuItem>
                      {mapping.categories.flatMap((c) => [
                        <ListSubheader key={`h-${c.id}`}>{c.label}</ListSubheader>,
                        ...(subsByCat[c.id] || []).map((s) => <MenuItem key={s.id} value={s.id} sx={{ pl: 3 }}>{s.label}</MenuItem>),
                      ])}
                    </Select>
                  </TableCell>
                </TableRow>
              ))}
              {tableProducts.length === 0 && (
                <TableRow><TableCell colSpan={2}><Typography variant="body2" color="text.secondary">Load a report first — products will appear here to map.</Typography></TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
        <Button variant="contained" startIcon={<Save />} onClick={doSave} disabled={saving}>
          {saving ? 'Saving…' : 'Save mapping'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default MassBalanceMapping;
